import { keepPreviousData, useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import {
  createBoardComment,
  createBoardPost,
  deleteBoardComment,
  deleteBoardPost,
  getBoardPost,
  getBoardPosts,
  updateBoardPost,
} from './apis'
import type { BoardPostDetail, BoardPostSummary, CreateBoardPostReq, GetBoardPostsReq, UpdateBoardPostReq } from './types'

/** 탭을 닫을 때 MenuOnSessionWrap 이 RP14 화면 쿼리 캐시를 지울 수 있게 메뉴 코드를 앞에 둔다. */
const PATH = ['RP14', 'board'] as const

export const boardQueryKeys = {
  all: PATH,
  lists: [...PATH, 'LIST'] as const,
  list: (params: GetBoardPostsReq | null) => [...PATH, 'LIST', params] as const,
  detail: (id: number) => [...PATH, 'DETAIL', id] as const,
}

export const useGetBoardPosts = (params: GetBoardPostsReq | null): UseQueryResult<ApiPageResponse<BoardPostSummary>, Error> =>
  useQuery({
    queryKey: boardQueryKeys.list(params),
    queryFn: async () => {
      if (!params) throw new Error('게시판 조회 조건이 없습니다.')
      return (await getBoardPosts(params)).data
    },
    enabled: params != null,
    placeholderData: keepPreviousData,
  })

/**
 * 부를 때마다 서버가 조회수를 올린다. 상세 화면은 열 때마다 새로 받고(1.0 과 같이 연 횟수만큼 오른다),
 * 수정 화면은 상세에서 받아 둔 캐시를 그대로 쓴다(`reuseCache`) — 상세 → 수정만으로 조회수가 오르지 않게.
 */
export const useGetBoardPost = (id: number, { reuseCache = false }: { reuseCache?: boolean } = {}): UseQueryResult<BoardPostDetail, Error> =>
  useQuery({
    queryKey: boardQueryKeys.detail(id),
    queryFn: async () => (await getBoardPost(id)).data,
    enabled: Number.isFinite(id),
    refetchOnWindowFocus: false,
    ...(reuseCache ? { staleTime: Infinity } : { refetchOnMount: 'always' as const }),
  })

/**
 * 쓰기 뒤 캐시 정리. 상세 조회는 조회수를 올리므로 필요한 때만 다시 받는다.
 * - 글 쓰기: 목록만 다시 받는다. 열려 있는 수정·상세 화면을 다시 조회하면 조회수가 오르고(수정) 지운 글은 404 알럿이 뜬다(삭제)
 * - 글 수정: 상세 캐시를 stale 로만 표시한다(refetchType 'none' — 지금 다시 받지 않는다). 수정 화면(reuseCache)이 옛 캐시로
 *   폼을 열지 않고 새로 받게 한다(LOGISPRD-17143). 이동한 상세는 refetchOnMount 'always' 로 새로 받는다
 * - 댓글: 상세(댓글 목록)·목록(댓글 수)을 다시 받는다 — 댓글 조회 API 가 따로 없어 이때 조회수가 1 오른다
 */
const useAfterWrite = (id: number | undefined, { detail }: { detail: 'keep' | 'stale' | 'reload' }) => {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: boardQueryKeys.lists }),
      id !== undefined && detail !== 'keep'
        ? queryClient.invalidateQueries({ queryKey: boardQueryKeys.detail(id), exact: true, refetchType: detail === 'stale' ? 'none' : 'active' })
        : undefined,
    ])
}

export const useCreateBoardPost = (): UseMutationResult<number, Error, CreateBoardPostReq> => {
  const afterWrite = useAfterWrite(undefined, { detail: 'keep' })
  return useMutation({ mutationFn: async (body) => (await createBoardPost(body)).data, onSuccess: afterWrite })
}

export const useUpdateBoardPost = (id: number): UseMutationResult<unknown, Error, UpdateBoardPostReq> => {
  const afterWrite = useAfterWrite(id, { detail: 'stale' })
  return useMutation({ mutationFn: (body) => updateBoardPost(id, body), onSuccess: afterWrite })
}

export const useDeleteBoardPost = (id: number): UseMutationResult<unknown, Error, void> => {
  const afterWrite = useAfterWrite(id, { detail: 'keep' })
  return useMutation({ mutationFn: () => deleteBoardPost(id), onSuccess: afterWrite })
}

export const useCreateBoardComment = (id: number): UseMutationResult<number, Error, string> => {
  const afterWrite = useAfterWrite(id, { detail: 'reload' })
  return useMutation({ mutationFn: async (comment) => (await createBoardComment(id, comment)).data, onSuccess: afterWrite })
}

export const useDeleteBoardComment = (id: number): UseMutationResult<unknown, Error, number> => {
  const afterWrite = useAfterWrite(id, { detail: 'reload' })
  return useMutation({ mutationFn: (commentId) => deleteBoardComment(id, commentId), onSuccess: afterWrite })
}
